/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

// Component imports
import { Form, Input, Select } from 'antd';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import { ACTIVITY_TYPES, VARIABLE_TYPES } from '../../../../constants/main';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import is from '../../../../utils/is';

const EmailNodeForm = () => {
  const { orgId, tenantId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, currentBotLanguage } = useBotLanguage();

  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));

  return (
    <Fragment>
      <Form.Item
        label={t('Recipient Email Address')}
        tooltip={t('Use comma to add more recipients.')}
        name={['data', 'email', 'address']}
        rules={[
          {
            required: true,
            message: t('Please add at least one address.')
          },
          {
            validator: async (rule, values) => {
              try {
                if (values) {
                  values.forEach(value => {
                    if (!is.email(value) && !is.variable(value)) {
                      throw new Error(rule.message);
                    }
                  });
                }
              } catch {
                throw new Error(rule.message);
              }
            },
            message: t('Please make sure the addresses are valid emails.')
          }]}
        // Ensure that whenever the form value changes, we trim each tag.
        normalize={(values) => {
          if (!values) {
            return values;
          }

          return values
            .map((item) => item.trim()) // Trim each entry
            .filter((item) => is.email(item) || is.variable(item)); // Ensure only valid emails or variables are kept
        }}
      >
        <Select
          mode="tags"
          className="w-100"
          placeholder={t('Enter email addresses')}
          size="large"
          disabled={!isEditingPrimaryLanguage}
          dropdownStyle={{ display: 'none' }}
          tokenSeparators={[',']}
        />
      </Form.Item>

      <Form.Item
        label={t('Subject')}
        name={['data', 'email', 'subject']}
      >
        <Input
          placeholder={t('Enter an email subject')}
          size="large"
          disabled={!isEditingPrimaryLanguage}
          maxLength={998} // According to RFC 2822, the maximum length of a line of text, including the <CRLF>, must not exceed 998 characters
        />
      </Form.Item>

      <Form.Item
        label={t('Body')}
        name={['data', 'email', 'body']}
      >
        <RichTextEditor
          placeholder={t('Enter the contents of the email')}
          shouldAutoFocus={false}
          disabled={!isEditingPrimaryLanguage}
          toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
          includedVariableTypes={activity?.type === ACTIVITY_TYPES.LIVECHAT_FLOW ? [VARIABLE_TYPES.LIVECHAT] : []}
        />
      </Form.Item>
    </Fragment>
  );
};

export default EmailNodeForm;
